'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Phone, Mail, Clock, ArrowLeft, Send, CheckCircle, AlertCircle, MessageSquare } from 'lucide-react';

interface FormState {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const initialForm: FormState = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
};

export default function ContactPage() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch('/api/send-contact-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setStatus('success');
        setForm(initialForm);
      } else {
        setStatus('error');
        setErrorMsg(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please try again.');
    }
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#0a0a0a' }}>
      <Header />
      <main className="flex-1 pt-16">
        {/* Hero */}
        <section className="border-b py-14 px-6" style={{ background: 'linear-gradient(135deg, #0d0d0d 0%, #111111 50%, #0a0a0a 100%)', borderColor: '#2a2a2a' }}>
          <div className="max-w-5xl mx-auto">
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-sm font-semibold transition-colors mb-6"
              style={{ color: '#8a7a65' }}
            >
              <ArrowLeft size={15} />
              Back to Home
            </Link>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'rgba(212,168,67,0.12)' }}>
                <MessageSquare size={24} style={{ color: '#d4a843' }} />
              </div>
              <span className="section-label">Get in Touch</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-3" style={{ color: '#f5f0e8' }}>
              Contact LP Refurbished
            </h1>
            <p className="text-base leading-relaxed max-w-2xl" style={{ color: '#8a7a65' }}>
              Have a question about a repair, a device, or just want to get in touch? We&apos;re here to help — reach out and we&apos;ll get back to you as soon as possible.
            </p>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-14 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

              {/* Left — Business Info */}
              <div className="lg:col-span-2 flex flex-col gap-6">

                {/* Contact Details Card */}
                <div className="rounded-3xl p-7 shadow-sm" style={{ backgroundColor: '#111111', border: '1px solid #2a2a2a' }}>
                  <h2 className="text-lg font-extrabold mb-5" style={{ color: '#f5f0e8' }}>Contact Details</h2>
                  <div className="flex flex-col gap-5">
                    <a
                      href="tel:0481446276"
                      className="flex items-start gap-4 group"
                    >
                      <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 transition-colors" style={{ backgroundColor: 'rgba(212,168,67,0.12)' }}>
                        <Phone size={18} style={{ color: '#d4a843' }} />
                      </div>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-widest mb-0.5" style={{ color: '#5a4a35' }}>Phone</p>
                        <p className="text-base font-bold transition-colors" style={{ color: '#f5f0e8' }}>0481 446 276</p>
                        <p className="text-xs mt-0.5" style={{ color: '#8a7a65' }}>Call or text anytime during business hours</p>
                      </div>
                    </a>

                    <a
                      href="mailto:lukapemberton@gmail.com"
                      className="flex items-start gap-4 group"
                    >
                      <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 transition-colors" style={{ backgroundColor: 'rgba(212,168,67,0.12)' }}>
                        <Mail size={18} style={{ color: '#d4a843' }} />
                      </div>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-widest mb-0.5" style={{ color: '#5a4a35' }}>Email</p>
                        <p className="text-base font-bold break-all transition-colors" style={{ color: '#f5f0e8' }}>lukapemberton@gmail.com</p>
                        <p className="text-xs mt-0.5" style={{ color: '#8a7a65' }}>We reply within 1 business day</p>
                      </div>
                    </a>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(212,168,67,0.12)' }}>
                        <Clock size={18} style={{ color: '#d4a843' }} />
                      </div>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-widest mb-0.5" style={{ color: '#5a4a35' }}>Service Area</p>
                        <p className="text-base font-bold" style={{ color: '#f5f0e8' }}>Australia-wide</p>
                        <p className="text-xs mt-0.5" style={{ color: '#8a7a65' }}>Mail-in service available nationwide</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Opening Hours Card */}
                <div className="rounded-3xl p-7 shadow-sm" style={{ backgroundColor: '#111111', border: '1px solid #2a2a2a' }}>
                  <h2 className="text-lg font-extrabold mb-5" style={{ color: '#f5f0e8' }}>Opening Hours</h2>
                  <div className="flex flex-col gap-2">
                    {[
                      { day: 'Monday', hours: '8:00 AM – 12:00 AM' },
                      { day: 'Tuesday', hours: '8:00 AM – 12:00 AM' },
                      { day: 'Wednesday', hours: '8:00 AM – 12:00 AM' },
                      { day: 'Thursday', hours: '8:00 AM – 12:00 AM' },
                      { day: 'Friday', hours: '8:00 AM – 12:00 AM' },
                      { day: 'Saturday', hours: '8:00 AM – 12:00 AM' },
                      { day: 'Sunday', hours: '8:00 AM – 12:00 AM' },
                    ].map(({ day, hours }) => (
                      <div key={day} className="flex items-center justify-between py-2 last:border-0" style={{ borderBottom: '1px solid #1a1a1a' }}>
                        <span className="text-sm font-semibold" style={{ color: '#f5f0e8' }}>{day}</span>
                        <span className="text-sm font-semibold" style={{ color: '#d4a843' }}>{hours}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 rounded-2xl px-4 py-3" style={{ backgroundColor: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.15)' }}>
                    <p className="text-xs font-bold" style={{ color: '#4ade80' }}>Open 7 days a week</p>
                    <p className="text-xs mt-0.5" style={{ color: '#4ade80', opacity: 0.7 }}>Including public holidays</p>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="rounded-3xl p-7" style={{ background: 'linear-gradient(135deg, #1a1500 0%, #221c00 100%)', border: '1px solid #3a2e00' }}>
                  <h2 className="text-base font-extrabold mb-4" style={{ color: '#f5f0e8' }}>Ready to book a repair?</h2>
                  <p className="text-sm mb-5 leading-relaxed" style={{ color: '#8a7a65' }}>
                    Skip the wait — book your repair directly and get a price estimate instantly.
                  </p>
                  <Link href="/repairs" className="btn-primary w-full justify-center text-sm py-3 block text-center">
                    Book a Repair
                  </Link>
                </div>
              </div>

              {/* Right — Contact Form */}
              <div className="lg:col-span-3">
                <div className="rounded-3xl p-8 shadow-sm" style={{ backgroundColor: '#111111', border: '1px solid #2a2a2a' }}>
                  <h2 className="text-lg font-extrabold mb-2" style={{ color: '#f5f0e8' }}>Send us a message</h2>
                  <p className="text-sm mb-7" style={{ color: '#8a7a65' }}>Fill in the form below and we&apos;ll get back to you as soon as possible.</p>

                  {status === 'success' ? (
                    <div className="flex flex-col items-center justify-center py-14 text-center">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5" style={{ backgroundColor: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.2)' }}>
                        <CheckCircle size={32} style={{ color: '#4ade80' }} />
                      </div>
                      <h3 className="text-xl font-extrabold mb-2" style={{ color: '#f5f0e8' }}>Message Sent!</h3>
                      <p className="text-sm max-w-sm leading-relaxed" style={{ color: '#8a7a65' }}>
                        Thanks for reaching out. We&apos;ll get back to you within 1 business day.
                      </p>
                      <button
                        onClick={() => setStatus('idle')}
                        className="mt-6 text-sm font-semibold hover:underline"
                        style={{ color: '#d4a843' }}
                      >
                        Send another message
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div className="flex flex-col gap-1.5">
                          <label htmlFor="name" className="text-xs font-bold uppercase tracking-widest" style={{ color: '#5a4a35' }}>
                            Full Name <span style={{ color: '#f87171' }}>*</span>
                          </label>
                          <input
                            id="name"
                            name="name"
                            type="text"
                            required
                            value={form.name}
                            onChange={handleChange}
                            placeholder="Your name"
                            className="w-full px-4 py-3 rounded-2xl text-sm font-medium transition-all"
                            style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a', color: '#f5f0e8', outline: 'none' }}
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label htmlFor="email" className="text-xs font-bold uppercase tracking-widest" style={{ color: '#5a4a35' }}>
                            Email Address <span style={{ color: '#f87171' }}>*</span>
                          </label>
                          <input
                            id="email"
                            name="email"
                            type="email"
                            required
                            value={form.email}
                            onChange={handleChange}
                            placeholder="you@example.com"
                            className="w-full px-4 py-3 rounded-2xl text-sm font-medium transition-all"
                            style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a', color: '#f5f0e8', outline: 'none' }}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div className="flex flex-col gap-1.5">
                          <label htmlFor="phone" className="text-xs font-bold uppercase tracking-widest" style={{ color: '#5a4a35' }}>
                            Phone Number
                          </label>
                          <input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={form.phone}
                            onChange={handleChange}
                            placeholder="04xx xxx xxx"
                            className="w-full px-4 py-3 rounded-2xl text-sm font-medium transition-all"
                            style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a', color: '#f5f0e8', outline: 'none' }}
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label htmlFor="subject" className="text-xs font-bold uppercase tracking-widest" style={{ color: '#5a4a35' }}>
                            Subject <span style={{ color: '#f87171' }}>*</span>
                          </label>
                          <select
                            id="subject"
                            name="subject"
                            required
                            value={form.subject}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-2xl text-sm font-medium transition-all cursor-pointer"
                            style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a', color: form.subject ? '#f5f0e8' : '#5a4a35', outline: 'none' }}
                          >
                            <option value="" style={{ backgroundColor: '#0d0d0d' }}>Select a subject</option>
                            <option value="repair-enquiry" style={{ backgroundColor: '#0d0d0d' }}>Repair Enquiry</option>
                            <option value="refurbished-phones" style={{ backgroundColor: '#0d0d0d' }}>Refurbished Phones</option>
                            <option value="warranty-claim" style={{ backgroundColor: '#0d0d0d' }}>Warranty Claim</option>
                            <option value="mail-in-repair" style={{ backgroundColor: '#0d0d0d' }}>Mail-In Repair</option>
                            <option value="general" style={{ backgroundColor: '#0d0d0d' }}>General Enquiry</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="message" className="text-xs font-bold uppercase tracking-widest" style={{ color: '#5a4a35' }}>
                          Message <span style={{ color: '#f87171' }}>*</span>
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          rows={5}
                          value={form.message}
                          onChange={handleChange}
                          placeholder="Tell us how we can help..."
                          className="w-full px-4 py-3 rounded-2xl text-sm font-medium transition-all resize-none"
                          style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a', color: '#f5f0e8', outline: 'none' }}
                        />
                      </div>

                      {status === 'error' && (
                        <div className="flex items-center gap-2 px-4 py-3 rounded-2xl text-sm" style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', color: '#f87171' }}>
                          <AlertCircle size={16} />
                          {errorMsg}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={status === 'loading'}
                        className="btn-primary w-full justify-center py-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {status === 'loading' ? (
                          <>Sending...</>
                        ) : (
                          <>
                            <Send size={16} />
                            Send Message
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Google Maps Embed */}
        <section className="py-14 px-6" style={{ borderTop: '1px solid #2a2a2a' }}>
          <div className="max-w-5xl mx-auto">
            <div className="mb-6">
              <h2 className="text-xl font-extrabold mb-1" style={{ color: '#f5f0e8' }}>Find Us</h2>
              <p className="text-sm" style={{ color: '#8a7a65' }}>We offer Australia-wide mail-in repairs. Drop off by appointment in the Sunshine Coast area.</p>
            </div>
            <div className="rounded-3xl overflow-hidden" style={{ border: '1px solid #2a2a2a', height: '380px' }}>
              <iframe
                title="LP Refurbished Location — Sunshine Coast, QLD"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d226957.6!2d152.9!3d-26.65!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b93f4e9e9e9e9e9%3A0x0!2sSunshine+Coast+QLD+Australia!5e0!3m2!1sen!2sau!4v1700000000000!5m2!1sen!2sau"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) brightness(0.85) contrast(1.1)' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <p className="text-xs mt-3" style={{ color: '#5a4a35' }}>
              📍 Sunshine Coast, QLD — Mail-in service available Australia-wide. Contact us to arrange a drop-off appointment.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
